import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import {
  Download,
  Upload,
  Database,
  RefreshCw,
  FileJson,
  FileSpreadsheet,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  HardDrive,
  Building2,
  Users,
  Package,
  Shield
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

export default function SuperAdminBackupDashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectiveImportDialogOpen, setSelectiveImportDialogOpen] = useState(false);
  const [importMode, setImportMode] = useState('merge');
  const [selectedFile, setSelectedFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [backupCollections, setBackupCollections] = useState([]);
  const [selectedCollections, setSelectedCollections] = useState([]);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/backup/stats');
      setStats(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des statistiques');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleExportAll = async () => {
    try {
      setExporting(true);
      const response = await api.get('/backup/export', {
        responseType: 'blob',
        timeout: 300000, // 5 min — export complet peut être lourd
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/json' }));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `hrengine_backup_${new Date().toISOString().slice(0,10)}.json`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=([^;]+)/);
        if (match) filename = match[1].trim().replace(/["']/g, '');
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export terminé avec succès');
    } catch (error) {
      console.error('Export error:', error);
      // Le détail peut être un Blob si responseType=blob — essayer de le lire
      let detail = 'Erreur lors de l\'export';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.detail) detail = parsed.detail;
        } catch (_) { /* ignore */ }
      } else if (error.response?.data?.detail) {
        detail = error.response.data.detail;
      } else if (error.code === 'ECONNABORTED') {
        detail = 'L\'export a dépassé le délai. Réessayez ou exportez par collection.';
      }
      toast.error(detail);
    } finally {
      setExporting(false);
    }
  };

  const handleExportCollection = async (collectionName, format = 'json') => {
    try {
      setExporting(true);
      const response = await api.get(`/backup/export/${collectionName}?format=${format}`, {
        responseType: 'blob',
        timeout: 180000, // 3 min
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${collectionName}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Export ${collectionName} terminé`);
    } catch (error) {
      console.error('Export collection error:', error);
      let detail = 'Erreur lors de l\'export';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.detail) detail = parsed.detail;
        } catch (_) { /* ignore */ }
      } else if (error.response?.data?.detail) {
        detail = error.response.data.detail;
      } else if (error.code === 'ECONNABORTED') {
        detail = 'L\'export a dépassé le délai.';
      }
      toast.error(detail);
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }
    
    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await api.post(`/backup/import?mode=${importMode}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setImportResult(response.data.results);
      toast.success('Import terminé');
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'import');
      console.error(error);
    } finally {
      setImporting(false);
    }
  };

  // Analyser le fichier de backup pour extraire les collections disponibles
  const handleFileSelect = async (file) => {
    setSelectedFile(file);
    setBackupCollections([]);
    setSelectedCollections([]);
    
    if (file && file.name.endsWith('.json')) {
      try {
        const text = await file.text();
        const data = JSON.parse(text);
        
        if (data.data) {
          const collections = Object.keys(data.data).map(name => ({
            name,
            count: Array.isArray(data.data[name]) ? data.data[name].length : 0
          }));
          setBackupCollections(collections);
        }
      } catch (error) {
        console.error('Error parsing backup file:', error);
      }
    }
  };

  // Import sélectif
  const handleSelectiveImport = async () => {
    if (!selectedFile) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }
    
    if (selectedCollections.length === 0) {
      toast.error('Veuillez sélectionner au moins une collection');
      return;
    }
    
    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const collectionsParam = selectedCollections.join(',');
      const response = await api.post(
        `/backup/import/selective?collections=${collectionsParam}&mode=${importMode}`, 
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setImportResult(response.data.results);
      toast.success(`Import sélectif terminé: ${selectedCollections.length} collection(s)`);
      fetchStats();
      setSelectiveImportDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'import sélectif');
      console.error(error);
    } finally {
      setImporting(false);
    }
  };

  const toggleCollectionSelection = (collName) => {
    setSelectedCollections(prev => 
      prev.includes(collName) 
        ? prev.filter(c => c !== collName)
        : [...prev, collName]
    );
  };

  const selectAllCollections = () => {
    setSelectedCollections(backupCollections.map(c => c.name));
  };

  const deselectAllCollections = () => {
    setSelectedCollections([]);
  };

  const getCollectionIcon = (name) => {
    const icons = {
      companies: Building2,
      users: Users,
      employees: Users,
      subscriptions: Package,
      superadmin_actions: Shield
    };
    return icons[name] || Database;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Sauvegarde & Restauration</h2>
          <p className="text-slate-600">Exportez et importez les données de la base de données</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={fetchStats} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Database className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {loading ? <Skeleton className="h-8 w-16" /> : stats?.totalDocuments?.toLocaleString() || 0}
                </p>
                <p className="text-sm text-slate-500">Documents totaux</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <HardDrive className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {loading ? <Skeleton className="h-8 w-16" /> : stats?.collections?.length || 0}
                </p>
                <p className="text-sm text-slate-500">Collections</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-blue-900">Export complet</p>
                <p className="text-sm text-blue-700">Télécharger toutes les données</p>
              </div>
              <Button onClick={handleExportAll} disabled={exporting}>
                {exporting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Exporter
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Export Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-5 h-5" />
              Exporter les données
            </CardTitle>
            <CardDescription>
              Téléchargez une sauvegarde complète ou par collection
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button 
              className="w-full justify-start" 
              variant="outline"
              onClick={handleExportAll}
              disabled={exporting}
            >
              <FileJson className="w-4 h-4 mr-2" />
              Export complet (JSON)
              <Badge className="ml-auto" variant="secondary">Recommandé</Badge>
            </Button>
            
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-2">Export par collection :</p>
              <div className="grid grid-cols-2 gap-2">
                {['companies', 'users', 'employees', 'subscriptions'].map(coll => (
                  <Button 
                    key={coll}
                    variant="ghost" 
                    size="sm"
                    className="justify-start"
                    onClick={() => handleExportCollection(coll, 'json')}
                    disabled={exporting}
                  >
                    <FileJson className="w-3 h-3 mr-1" />
                    {coll}
                  </Button>
                ))}
              </div>
            </div>
            
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-2">Export CSV (tableur) :</p>
              <div className="grid grid-cols-2 gap-2">
                {['employees', 'users', 'companies'].map(coll => (
                  <Button 
                    key={coll}
                    variant="ghost" 
                    size="sm"
                    className="justify-start"
                    onClick={() => handleExportCollection(coll, 'csv')}
                    disabled={exporting}
                  >
                    <FileSpreadsheet className="w-3 h-3 mr-1" />
                    {coll}.csv
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Import Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Importer les données
            </CardTitle>
            <CardDescription>
              Restaurez une sauvegarde précédente
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800">Attention</p>
                  <p className="text-amber-700">
                    L&apos;import peut modifier ou remplacer des données existantes.
                    Assurez-vous d&apos;avoir une sauvegarde récente.
                  </p>
                </div>
              </div>
            </div>
            
            <Button 
              className="w-full" 
              variant="outline"
              onClick={() => setImportDialogOpen(true)}
            >
              <Upload className="w-4 h-4 mr-2" />
              Importer tout depuis un fichier JSON
            </Button>
            
            <Button 
              className="w-full bg-blue-600 hover:bg-blue-700 text-white" 
              onClick={() => setSelectiveImportDialogOpen(true)}
            >
              <Database className="w-4 h-4 mr-2" />
              Import sélectif (choisir les tables)
            </Button>
            
            <p className="text-xs text-slate-500 text-center">
              L&apos;import sélectif permet de choisir quelles tables importer depuis un fichier de sauvegarde.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Collections Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            Collections de la base de données
          </CardTitle>
          <CardDescription>
            Détail des données stockées par collection
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Collection</TableHead>
                  <TableHead className="text-right">Documents</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats?.collections?.map((coll) => {
                  const Icon = getCollectionIcon(coll.name);
                  return (
                    <TableRow key={coll.name}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">{coll.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant="outline">
                          {coll.documents?.toLocaleString() || 0}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleExportCollection(coll.name, 'json')}
                            disabled={exporting || coll.documents === 0}
                          >
                            <FileJson className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleExportCollection(coll.name, 'csv')}
                            disabled={exporting || coll.documents === 0}
                          >
                            <FileSpreadsheet className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Importer des données</DialogTitle>
            <DialogDescription>
              Sélectionnez un fichier JSON de sauvegarde et le mode d&apos;import
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Fichier de sauvegarde</Label>
              <Input
                type="file"
                accept=".json"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              />
              {selectedFile && (
                <p className="text-sm text-green-600 flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4" />
                  {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} Ko)
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Mode d&apos;import</Label>
              <Select value={importMode} onValueChange={setImportMode}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="merge">
                    Fusion - Mettre à jour les existants, ajouter les nouveaux
                  </SelectItem>
                  <SelectItem value="skip_existing">
                    Ignorer existants - Ajouter uniquement les nouveaux
                  </SelectItem>
                  <SelectItem value="replace">
                    Remplacer - Supprimer tout et remplacer (⚠️ Dangereux)
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {importMode === 'replace' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5" />
                  Le mode &quot;Remplacer&quot; supprimera TOUTES les données existantes avant l&apos;import.
                  Cette action est irréversible.
                </p>
              </div>
            )}
            
            {importResult && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm font-medium text-green-800 mb-2">Résultat de l&apos;import :</p>
                <ul className="text-sm text-green-700 space-y-1">
                  {importResult.imported?.map((r, i) => (
                    <li key={i}>
                      ✅ {r.collection}: {r.inserted} ajoutés, {r.updated} mis à jour
                    </li>
                  ))}
                  {importResult.errors?.map((r, i) => (
                    <li key={i} className="text-red-600">
                      ❌ {r.collection}: {r.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setImportDialogOpen(false);
              setSelectedFile(null);
              setImportResult(null);
            }}>
              Annuler
            </Button>
            <Button 
              onClick={handleImport} 
              disabled={!selectedFile || importing}
              variant={importMode === 'replace' ? 'destructive' : 'default'}
            >
              {importing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Importer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Selective Import Dialog */}
      <Dialog open={selectiveImportDialogOpen} onOpenChange={(open) => {
        setSelectiveImportDialogOpen(open);
        if (!open) {
          setSelectedFile(null);
          setBackupCollections([]);
          setSelectedCollections([]);
          setImportResult(null);
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-600" />
              Import sélectif de données
            </DialogTitle>
            <DialogDescription>
              Choisissez les tables (collections) à importer depuis votre fichier de sauvegarde.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* File Selection */}
            <div className="space-y-2">
              <Label>Fichier de sauvegarde</Label>
              <Input
                type="file"
                accept=".json"
                onChange={(e) => handleFileSelect(e.target.files?.[0])}
                className="cursor-pointer"
              />
            </div>
            
            {/* Collections Selection */}
            {backupCollections.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Collections disponibles ({backupCollections.length})</Label>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={selectAllCollections}>
                      Tout sélectionner
                    </Button>
                    <Button variant="ghost" size="sm" onClick={deselectAllCollections}>
                      Désélectionner
                    </Button>
                  </div>
                </div>
                
                <ScrollArea className="h-[250px] border rounded-lg p-3">
                  <div className="grid grid-cols-2 gap-2">
                    {backupCollections.map((coll) => (
                      <div 
                        key={coll.name}
                        className={`flex items-center justify-between p-2 rounded-lg border cursor-pointer hover:bg-slate-50 ${
                          selectedCollections.includes(coll.name) ? 'bg-blue-50 border-blue-300' : ''
                        }`}
                        onClick={() => toggleCollectionSelection(coll.name)}
                      >
                        <div className="flex items-center gap-2">
                          <Checkbox 
                            checked={selectedCollections.includes(coll.name)}
                            onCheckedChange={() => toggleCollectionSelection(coll.name)}
                          />
                          <span className="text-sm font-medium">{coll.name}</span>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {coll.count}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
                
                <p className="text-sm text-slate-600">
                  {selectedCollections.length} collection(s) sélectionnée(s)
                </p>
              </div>
            )}
            
            {/* Import Mode */}
            <div className="space-y-2">
              <Label>Mode d&apos;import</Label>
              <Select value={importMode} onValueChange={setImportMode}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="merge">
                    Fusionner - Mettre à jour existants + ajouter nouveaux
                  </SelectItem>
                  <SelectItem value="skip_existing">
                    Ignorer existants - Ajouter uniquement les nouveaux
                  </SelectItem>
                  <SelectItem value="replace">
                    Remplacer - Supprimer et remplacer (⚠️ Dangereux)
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {importMode === 'replace' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5" />
                  Le mode &quot;Remplacer&quot; supprimera les données des collections sélectionnées avant l&apos;import.
                </p>
              </div>
            )}
            
            {/* Import Result */}
            {importResult && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm font-medium text-green-800 mb-2">Résultat de l&apos;import :</p>
                <ul className="text-sm text-green-700 space-y-1 max-h-[150px] overflow-auto">
                  {importResult.imported?.map((r, i) => (
                    <li key={i}>
                      ✅ {r.collection}: {r.inserted || 0} ajoutés, {r.updated || 0} mis à jour
                    </li>
                  ))}
                  {importResult.skipped?.map((r, i) => (
                    <li key={i} className="text-amber-600">
                      ⏭️ {r.collection}: {r.reason}
                    </li>
                  ))}
                  {importResult.errors?.map((r, i) => (
                    <li key={i} className="text-red-600">
                      ❌ {r.collection}: {r.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setSelectiveImportDialogOpen(false);
              setSelectedFile(null);
              setBackupCollections([]);
              setSelectedCollections([]);
              setImportResult(null);
            }}>
              Annuler
            </Button>
            <Button 
              onClick={handleSelectiveImport} 
              disabled={!selectedFile || selectedCollections.length === 0 || importing}
              variant={importMode === 'replace' ? 'destructive' : 'default'}
            >
              {importing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Importer ({selectedCollections.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
